import { useState, useCallback } from 'react';
import { router, Link } from '@inertiajs/react';
import GmLayout from '@/layouts/GmLayout';
import { GmItemsAwardedProps, Item, PaginationLink } from '@/types/items';

/**
 * Format date in a readable format.
 */
function formatDate(isoString: string | null): string {
    if (!isoString) return 'N/A';
    return new Date(isoString).toLocaleDateString();
}

/**
 * GM Awarded Items page - displays all awarded item instances.
 */
export default function Awarded({ items, filters }: GmItemsAwardedProps) {
    const [search, setSearch] = useState(filters.search || '');
    const [qualityFilter, setQualityFilter] = useState(filters.quality || '');

    // Handle search/filter submission
    const handleFilter = useCallback(() => {
        router.get('/gm/items/awarded', {
            search: search || undefined,
            quality: qualityFilter || undefined,
        }, { preserveState: true });
    }, [search, qualityFilter]);

    // Clear all filters
    const handleClearFilters = () => {
        setSearch('');
        setQualityFilter('');
        router.get('/gm/items/awarded', {}, { preserveState: true });
    };

    return (
        <GmLayout title="Awarded Items" activePath="/gm/items">
            <div className="max-w-7xl">
                <div className="flex items-center justify-between mb-6">
                    <h1 className="text-2xl font-bold text-white">Awarded Items</h1>
                    <div className="flex items-center gap-4">
                        <Link
                            href="/gm/items"
                            className="px-4 py-2 bg-slate-600 hover:bg-slate-500 text-white rounded transition-colors"
                        >
                            ← Back to Recipes
                        </Link>
                        <Link
                            href="/gm/items/award"
                            className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded transition-colors"
                        >
                            + Award Item
                        </Link>
                    </div>
                </div>

                {/* Search and Filters */}
                <div className="bg-slate-800 border border-slate-700 rounded-lg p-4 mb-6">
                    <div className="flex flex-wrap gap-4 items-end">
                        {/* Search Input */}
                        <div className="flex-1 min-w-[200px]">
                            <label className="block text-sm text-gray-400 mb-1">Search</label>
                            <input
                                type="text"
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                onKeyDown={(e) => e.key === 'Enter' && handleFilter()}
                                placeholder="Item name or recipient username"
                                className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded text-white placeholder-gray-400"
                            />
                        </div>

                        {/* Quality Filter */}
                        <div className="w-40">
                            <label className="block text-sm text-gray-400 mb-1">Quality</label>
                            <select
                                value={qualityFilter}
                                onChange={(e) => setQualityFilter(e.target.value)}
                                className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded text-white"
                            >
                                <option value="">All Qualities</option>
                                {[1, 2, 3, 4, 5].map((q) => (
                                    <option key={q} value={q}>
                                        {'★'.repeat(q)}
                                    </option>
                                ))}
                            </select>
                        </div>

                        {/* Filter Buttons */}
                        <div className="flex gap-2">
                            <button
                                onClick={handleFilter}
                                className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded transition-colors"
                            >
                                Apply
                            </button>
                            <button
                                onClick={handleClearFilters}
                                className="px-4 py-2 bg-slate-600 hover:bg-slate-500 text-white rounded transition-colors"
                            >
                                Clear
                            </button>
                        </div>
                    </div>
                </div>

                {/* Items Table */}
                {items.data.length === 0 ? (
                    <div className="text-center py-12 text-gray-400">
                        No awarded items found.
                    </div>
                ) : (
                    <div className="bg-slate-800 border border-slate-700 rounded-lg overflow-hidden">
                        <div className="overflow-x-auto">
                            <table className="w-full">
                                <thead className="bg-slate-700">
                                    <tr>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">Item</th>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">Quality</th>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">Stats</th>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">Owner</th>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">Awarded</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-700">
                                    {items.data.map((item) => (
                                        <ItemRow key={item.id} item={item} />
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}

                {/* Pagination */}
                {items.last_page > 1 && (
                    <Pagination links={items.links} />
                )}
            </div>
        </GmLayout>
    );
}

/**
 * ItemRow - Displays a single awarded item row.
 */
function ItemRow({ item }: { item: Item }) {
    return (
        <tr className={item.is_equipped ? 'bg-green-900/20' : ''}>
            {/* Item Name */}
            <td className="px-4 py-3">
                <div>
                    <p className={`font-medium ${item.rarity.tailwindClass}`}>{item.name}</p>
                    <p className="text-xs text-gray-500">{item.rarity.label}</p>
                </div>
            </td>

            {/* Quality */}
            <td className="px-4 py-3">
                <span className="text-yellow-400">{item.quality_stars}</span>
            </td>

            {/* Stats */}
            <td className="px-4 py-3">
                <div className="text-sm">
                    {Object.keys(item.stats).length > 0 ? (
                        <>
                            {Object.entries(item.stats).map(([key, stat]) => (
                                <span key={key} className="text-gray-300 mr-2">
                                    +{stat.value} {stat.label}
                                </span>
                            ))}
                            <span className="text-gray-500 text-xs">(Total: +{item.total_stat_bonus})</span>
                        </>
                    ) : item.recipe_fixed_stats ? (
                        <>
                            {Object.entries(item.recipe_fixed_stats).map(([key, stat]) => (
                                <span key={key} className="text-gray-300 mr-2">
                                    +{stat.value} {stat.label}
                                </span>
                            ))}
                            <span className="text-gray-500 text-xs">
                                (Total: +{Object.values(item.recipe_fixed_stats).reduce((sum, stat) => sum + stat.value, 0)})
                            </span>
                        </>
                    ) : item.recipe_allowed_stats ? (
                        <span className="text-gray-500 italic">
                            {Object.keys(item.recipe_allowed_stats).length} possible bonuses
                        </span>
                    ) : (
                        <span className="text-gray-500">No stats</span>
                    )}
                </div>
            </td>

            {/* Owner */}
            <td className="px-4 py-3">
                <div className="text-sm">
                    <p className="text-white">{item.character_name || 'Unequipped'}</p>
                    {item.is_equipped && (
                        <span className="text-xs text-green-400">Equipped</span>
                    )}
                </div>
            </td>

            {/* Awarded Info */}
            <td className="px-4 py-3 text-sm text-gray-400">
                <p>{formatDate(item.awarded_at)}</p>
                {item.awarded_by && (
                    <p className="text-xs text-gray-500">by {item.awarded_by}</p>
                )}
            </td>
        </tr>
    );
}

/**
 * Pagination - Displays pagination links.
 */
function Pagination({ links }: { links: PaginationLink[] }) {
    return (
        <div className="mt-6 flex justify-center gap-1">
            {links.map((link, index) => (
                <a
                    key={index}
                    href={link.url || '#'}
                    className={`px-3 py-1 rounded text-sm ${
                        link.active
                            ? 'bg-purple-600 text-white'
                            : link.url
                            ? 'bg-slate-700 text-gray-300 hover:bg-slate-600'
                            : 'bg-slate-800 text-gray-500 cursor-not-allowed'
                    }`}
                    dangerouslySetInnerHTML={{ __html: link.label }}
                />
            ))}
        </div>
    );
}

